<template>
  <v-col cols="12">
    <v-form ref="myform">
      <v-container class="py-0">
        <v-toolbar-title>{{ $t('keywords.register') }}</v-toolbar-title>
        <v-row>
        <v-col cols="12" xl="2" lg="2" md="4" sm="6" xs="12">
          <v-switch :label="$t('check.active')" v-model="workflow.active" />
          <v-switch :label="$t('workflow.form.show_url')" v-model="showUrl" v-if="workflow.integrationType==='REST'"/>
        </v-col>
        </v-row>
        <v-row v-if="workflow.integrationType==='REST'" >
          <v-col cols="12" >
            <v-card class="pl-0" v-if="showUrl">
              <v-card-text>
                  <div  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" color="teal" text @click="copyUrl"> <v-icon class="mr-2" >mdi-content-copy</v-icon></v-btn>
                      </template>
                      <span>{{$t('workflow.copy')}}</span>
                    </v-tooltip>
                    <span>{{getUrl}}</span>
                  </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="3">
            <v-text-field
              :readonly="isUpdating"
              name="workflowName"
              :label="$t('workflow.form.workflow_name')"
              v-model="workflow.workflowName"
              :rules="alphaNumericRules(workflow.workflowName)"
            />
          </v-col>
          <v-col cols="12" lg="2">
            <v-select
              name="integrationType"
              :label="$t('workflow.form.integration_type')"
              :items="integrationTypes"
              v-model="workflow.integrationType"
              :rules="requiredRules(workflow.integrationType)"
            />
          </v-col>
          <v-col cols="12" lg="2" v-if="isPlatform">
            <RoutingkeySelect v-model="workflow.routingkey" :rules="requiredRules(workflow.routingkey)" />
          </v-col>
          <v-col v-if="workflow.integrationType === 'JOB'" cols="12" lg="2">
            <CronField v-model="workflow.starterAction.cron" />
          </v-col>
          <v-col cols="12" lg="3" v-if="$versionable.versions.workflow > '1.0.0' && (workflow.integrationType === 'JOB' || workflow.integrationType === 'REST') && isPlatform" >
            <v-text-field
              v-model="workflow.maxFailuresBeforeDeactivation"
              type="number"
              :label="$t('workflow.form.failure_limit.label')"
              :disabled="!containNotificationServer"
              :hint="maxFailuresHint"
              :persistent-hint="!containNotificationServer"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" xl="12" lg="12" md="12" sm="12" xs="12">
            <v-btn width="100%" color="gray" class="mr-0" @click.prevent="toggle">
              {{ $t('workflow.form.workflow_description') }} <v-icon>{{active ? 'mdi-arrow-up' : 'mdi-arrow-down'}}</v-icon>
            </v-btn>
            <VueAccordion :expanded="active">
              <ckeditor
              editor="ClassicEditor"
              :config="editorConfig"
              id="editor1"
              name="workflowDescription"
              :label="$t('workflow.form.workflow_description')"
              v-model="workflow.workflowDescription"
              />
            </VueAccordion>
          </v-col>
        </v-row>
        <v-row v-if="workflow.integrationType === 'REST'">
          <v-col cols="12" md="4">
            <HttpMethodSelect
              :label="$t('workflow.form.http_method')"
              v-model="workflow.starterAction.restMethod"
              :required="true"
            />
          </v-col>
          <v-col cols="12" md="3">
            <AuthenticationSelect v-model="workflow.starterAction.wsAuthorizationType" :rules="requiredRules(workflow.starterAction.wsAuthorizationType)" />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-if="workflow.starterAction.wsAuthorizationType === 'Bearer'"
              name="wsAuthorizationToken"
              :label="$t('keywords.authentication.token')"
              v-model="workflow.starterAction.wsAuthorizationToken"
            />
            <CredentialSelect
              v-else-if="workflow.starterAction.wsAuthorizationType === 'Basic'"
              name="credentialName"
              :label="$t('keywords.authentication.credential')"
              v-model="workflow.starterAction.credentialName"
              type="AUTHORIZATION_WS"
            />
          </v-col>
        </v-row>

        <!-- Linha com o único bean de workflow para manter compatibilidade com legado -->
        <v-row v-if="$versionable.versions.workflow <= '1.0.0'">
          <v-col cols="12">
            <BeanSelect
              :label="$t('keywords.bean.authentication')"
              v-model="workflow.starterAction.wsAuthorizationBean"
              classtype="br.com.senior.plutaoapp.utils.process.AuthenticationExecutor"
            />
          </v-col>
        </v-row>
        <!------------------------------------------------------------------------------->

        <v-row>
          <v-row align="center">
            <v-btn color="teal" text @click="tryPlay">
              <v-icon class="mr-2">mdi-animation-play</v-icon>
              {{ $t('button.play') }}
            </v-btn>
            <v-checkbox v-model="closeOnSave" :label="$t('check.quit_on_save')"></v-checkbox>
          </v-row>
          <v-row align="center" justify="end">
            <v-btn class="mr-1" color="primary" @click="save">{{ $t('button.save') }}</v-btn>
            <v-btn color="blue darken-1" text @click="close">{{ $t('button.cancel') }}</v-btn>
          </v-row>
        </v-row>
      </v-container>
    </v-form>
  </v-col>
</template>

<script>
import VueAccordion from '@ztrehagem/vue-accordion'
import CKEditor from 'ckeditor4-vue'
import Vue from 'vue'
import EmailServerService from '../../../services/EmailServerService'
import LocalStorageService from '../../../services/LocalStorageService'
import cronExpression from '../../../utils/CronExpression'
import { alphaNumericRules, requiredRules } from '../../../utils/rules'
import AuthenticationSelect from '../../core/AuthenticationSelect.vue'
import BeanSelect from '../../core/BeanSelect'
import CredentialSelect from '../../core/CredentialSelect'
import CronField from '../../core/CronField'
import HttpMethodSelect from '../../core/HttpMethodSelect'
import RoutingkeySelect from '../../core/RoutingkeySelect.vue'

Vue.use(CKEditor)

export default {
  name: 'HeaderMainForm',
  components: {
    BeanSelect,
    CredentialSelect,
    HttpMethodSelect,
    VueAccordion,
    AuthenticationSelect,
    RoutingkeySelect,
    CronField
  },

  data: () => ({
    alphaNumericRules,
    requiredRules,
    closeOnSave: false,
    showUrl: true,
    editorConfig: {
      toolbarGroups: [{
        name: 'basicstyles',
        groups: ['basicstyles']
      },
      {
        name: 'links',
        groups: ['links']
      },
      {
        name: 'paragraph',
        groups: ['list', 'blocks', '-', 'JustifyLeft']
      },
      {
        name: 'document',
        groups: ['mode']
      },
      {
        name: 'insert',
        groups: ['insert']
      },
      {
        name: 'styles',
        groups: ['styles']
      },
      {
        name: 'about',
        groups: ['about']
      }
      ],
      removeButtons: 'Strike,Subscript,Superscript,Anchor,Styles,Specialchar,PasteFromWord',
      readOnly: false,
      extraPlugins: 'justify'

    },
    alertShow: false,
    alertType: 'info',
    cronExpressionResult: '',
    active: false,
    containNotificationServer: false,
    isPlatform: LocalStorageService.isPlatform()
  }),

  props: {
    workflow: Object,
    isUpdating: Boolean,
    integrationTypes: Array
  },

  model: {
    prop: 'workflow'
  },

  computed: {
    getUrl () {
      const host = LocalStorageService.getAccessUrl()
      const clientPrefix = LocalStorageService.getClientPrefix()
      const workflowName = this.workflow.workflowName
      return `${host}/${clientPrefix}/workflowrest/${workflowName}`
    },
    maxFailuresHint () {
      return this.containNotificationServer ? this.$t('workflow.form.failure_limit.hint.hasNotificationServers') : this.$t('workflow.form.failure_limit.hint.hasntNotificationServers')
    }
  },
  mounted () {
    this.setContainNotificationServer()
  },
  methods: {
    toggle () {
      this.active = !this.active
    },
    copyUrl () {
      navigator.clipboard.writeText(this.getUrl)
    },
    setContainNotificationServer () {
      EmailServerService.notificationServers()
        .then(r => { this.containNotificationServer = r.data ? r.data.length > 0 : false })
    },
    async save () {
      const event = {
        valid: false,
        closeOnSave: this.closeOnSave
      }
      /* if (this.workflow.integrationType === 'JOB') {
        await this.verifyCronExpression().then((res) => {
          if (res) {
            if (this.$refs.myform.validate()) {
              event.valid = true
            } else {
              this.$message.error(this.$t('warnings.validation.fill_required_fields'))
            }
          } else {
            this.$message.error(this.$t('workflow.form.incorrect_cron'))
          }
          this.$emit('save', event)
        })
      } else { */
      if (this.$refs.myform.validate()) {
        event.valid = true
      } else {
        this.$message.error(this.$t('warnings.validation.fill_required_fields'))
      }
      this.$emit('save', event)
      /* } */
    },

    close () {
      this.$emit('close')
    },

    async verifyCronExpression () {
      if (this.workflow.starterAction.cron) {
        let cronValid = false
        await cronExpression(this.workflow.starterAction.cron).then(
          (result) => {
            if (result && result.data && result.data.description) {
              this.cronExpressionResult = result.data.description
              cronValid = true
            } else {
              this.cronExpressionResult = result.data.error
            }
          }
        )
        return cronValid
      }
    },

    tryPlay () {
      this.$emit('play', this.workflow)
    }
  }
}

</script>

<style scoped>
.right {
  float: right;
}
</style>
